import { Tooltip } from "antd";
import "./InfoTooltip.scss";
import { AbstractTooltipProps } from "antd/lib/tooltip";
import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { QuestionMark } from "../icons-and-animations/Icons";

const DEFAULT_HOVER_TOOLTIP_ENTER_DELAY = 0.7;
const FAST_HOVER_TOOLTIP_ENTER_DELAY = 0;
const HOVER_TOOLTIP_FAST_WINDOW_MS = 100;

let openHoverTooltipCount = 0;
let lastHoverTooltipClosedAt = 0;
let hoverTooltipFastWindowTimer: number | undefined;
const hoverTooltipTimingSubscribers = new Set<() => void>();

type HoverTooltipMediaSource = {
  type: "image" | "gif" | "video";
  src: string;
  alt?: string;
  poster?: string;
};

type HoverTooltipMedia = ReactNode | HoverTooltipMediaSource;

interface HoverTooltipContentProps {
  title?: ReactNode;
  description?: ReactNode;
  media?: HoverTooltipMedia;
}

interface HoverTooltipProps extends Omit<AbstractTooltipProps, "title"> {
  title?: ReactNode;
  description?: ReactNode;
  media?: HoverTooltipMedia;
  immediate?: boolean;
  children: ReactNode;
}

interface Props extends Omit<AbstractTooltipProps, "title"> {
  info: string;
  title?: ReactNode;
  media?: HoverTooltipMedia;
  immediate?: boolean;
  style?: CSSProperties;
  tooltipStyle?: CSSProperties;
  outline?: boolean;
}

function notifyHoverTooltipTimingSubscribers(): void {
  hoverTooltipTimingSubscribers.forEach((subscriber) => subscriber());
}

function clearHoverTooltipFastWindowTimer(): void {
  if (hoverTooltipFastWindowTimer === undefined) return;
  window.clearTimeout(hoverTooltipFastWindowTimer);
  hoverTooltipFastWindowTimer = undefined;
}

function scheduleHoverTooltipFastWindowReset(): void {
  if (typeof window === "undefined") return;
  clearHoverTooltipFastWindowTimer();
  hoverTooltipFastWindowTimer = window.setTimeout(() => {
    hoverTooltipFastWindowTimer = undefined;
    notifyHoverTooltipTimingSubscribers();
  }, HOVER_TOOLTIP_FAST_WINDOW_MS);
}

function getHoverTooltipEnterDelay(): number {
  if (openHoverTooltipCount > 0) {
    return FAST_HOVER_TOOLTIP_ENTER_DELAY;
  }

  if (
    lastHoverTooltipClosedAt > 0 &&
    Date.now() - lastHoverTooltipClosedAt < HOVER_TOOLTIP_FAST_WINDOW_MS
  ) {
    return FAST_HOVER_TOOLTIP_ENTER_DELAY;
  }

  return DEFAULT_HOVER_TOOLTIP_ENTER_DELAY;
}

function useHoverTooltipEnterDelay(): number {
  const [, setRevision] = useState(0);

  useEffect(() => {
    const subscriber = () => setRevision((revision) => revision + 1);
    hoverTooltipTimingSubscribers.add(subscriber);
    return () => {
      hoverTooltipTimingSubscribers.delete(subscriber);
    };
  }, []);

  return getHoverTooltipEnterDelay();
}

function setHoverTooltipOpenState(wasOpen: boolean, nextOpen: boolean): void {
  if (wasOpen === nextOpen) return;

  if (nextOpen) {
    openHoverTooltipCount += 1;
    clearHoverTooltipFastWindowTimer();
  } else {
    openHoverTooltipCount = Math.max(0, openHoverTooltipCount - 1);
    lastHoverTooltipClosedAt = Date.now();
    scheduleHoverTooltipFastWindowReset();
  }

  notifyHoverTooltipTimingSubscribers();
}

function isHoverTooltipMediaSource(
  media: HoverTooltipMedia
): media is HoverTooltipMediaSource {
  return Boolean(
    media && typeof media === "object" && "src" in media && "type" in media
  );
}

function renderHoverTooltipMedia(media: HoverTooltipMedia): ReactNode {
  if (!media) return null;
  if (!isHoverTooltipMediaSource(media)) {
    return media;
  }

  if (media.type === "video") {
    return (
      <video
        className="HoverTooltip__media"
        src={media.src}
        poster={media.poster}
        muted
        loop
        playsInline
        autoPlay
      />
    );
  }

  return (
    <img
      className="HoverTooltip__media"
      src={media.src}
      alt={media.alt ?? ""}
    />
  );
}

export function HoverTooltipContent({
  title,
  description,
  media,
}: HoverTooltipContentProps): JSX.Element {
  return (
    <div className="HoverTooltip">
      {media ? (
        <div className="HoverTooltip__media-wrap">
          {renderHoverTooltipMedia(media)}
        </div>
      ) : null}
      {title ? <div className="HoverTooltip__title">{title}</div> : null}
      {description ? (
        <div className="HoverTooltip__description">{description}</div>
      ) : null}
    </div>
  );
}

export function HoverTooltip({
  title,
  description,
  media,
  children,
  placement = "bottom",
  overlayClassName,
  onOpenChange,
  mouseEnterDelay,
  immediate = false,
  ...rest
}: HoverTooltipProps): JSX.Element {
  const openRef = useRef(false);
  const sharedMouseEnterDelay = useHoverTooltipEnterDelay();
  const effectiveMouseEnterDelay = immediate
    ? FAST_HOVER_TOOLTIP_ENTER_DELAY
    : mouseEnterDelay ?? sharedMouseEnterDelay;

  useEffect(() => {
    return () => {
      setHoverTooltipOpenState(openRef.current, false);
      openRef.current = false;
    };
  }, []);

  function handleOpenChange(nextOpen: boolean): void {
    setHoverTooltipOpenState(openRef.current, nextOpen);
    openRef.current = nextOpen;
    onOpenChange?.(nextOpen);
  }

  return (
    <Tooltip
      title={
        <HoverTooltipContent
          title={title}
          description={description}
          media={media}
        />
      }
      placement={placement}
      overlayClassName={["HoverTooltipOverlay", overlayClassName]
        .filter(Boolean)
        .join(" ")}
      arrow={{ pointAtCenter: true }}
      mouseEnterDelay={effectiveMouseEnterDelay}
      onOpenChange={handleOpenChange}
      {...rest}
    >
      {children}
    </Tooltip>
  );
}

export function InfoTooltip({
  info,
  title,
  media,
  immediate = true,
  style,
  tooltipStyle,
  outline,
  ...rest
}: Props): JSX.Element {
  return (
    <span className={"InfoTooltip"} style={{ cursor: "help", ...style }}>
      <HoverTooltip
        title={title}
        description={info}
        media={media}
        immediate={immediate}
        style={tooltipStyle}
        placement={"right"}
        {...rest}
      >
        <div>
          <QuestionMark />
        </div>
      </HoverTooltip>
    </span>
  );
}
